import { autorun, observable, runInAction } from 'mobx';
import API from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';
import RootStore from './RootStore';

export interface ServiceNumber {
  number: string;
  priceType: string;
  price?: string;
  clearingType?: number;
  serviceProvider: string;
  serviceName: string;
  mobileOpened: boolean;
  fixOpened: boolean;
  roamingOpened: boolean;
  priceDescription?: string[];
  extraInfos?: string[];
  supplementaryNumbers?: string[];
}

export interface ServiceNumberList {
  serviceNumbers?: ServiceNumber[];
  status: FetchStatus;
}

class BillingStore {
  @observable accessor numberList: ServiceNumberList = {
    status: FetchStatus.UNINITIALIZED,
  };

  private disposeLiveUpdate: () => void;

  constructor(rootStore: RootStore) {
    if (rootStore.initialState?.serviceNumbers) {
      this.numberList = {
        serviceNumbers: rootStore.initialState.serviceNumbers,
        status: FetchStatus.FETCHED,
      };
    }
  }

  public subscribe() {
    if (!this.disposeLiveUpdate) {
      this.disposeLiveUpdate = autorun(() => this.liveUpdate());
    }
  }

  public unsubscribe() {
    if (this.disposeLiveUpdate) {
      this.disposeLiveUpdate();
      delete this.disposeLiveUpdate;
    }
  }

  public liveUpdate() {
    if (!this.numberList.serviceNumbers) {
      return this.fetchServiceNumbers();
    }
  }

  public fetchServiceNumbers = async (): Promise<void> => {
    runInAction(() => {
      this.numberList.status = FetchStatus.FETCHING;
    });
    try {
      const result = await API.get('/billing/servicenumbers');
      runInAction(() => {
        this.numberList = result.data;
        this.numberList.status = FetchStatus.FETCHED;
      });
    } catch (_e) {
      runInAction(() => {
        this.numberList.status = FetchStatus.ERROR;
      });
    }
  };

  public isNumbersFetching() {
    return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(this.numberList.status);
  }
}

export default BillingStore;
